// HTTP client for the consolidated `trips` Worker. All requests carry
// the CF Access cookie via `credentials: 'include'`; the Worker
// validates the JWT in src/lib/cf-access-jwt.js. Same API mobile uses
// — when this client and the mobile client both call PATCH /api/trips/
// {tripId}/meta with new stays, the Worker's applyTripMetaPatch merges
// them through the same code path.

import type {
  CatalogEntity,
  CatalogManifest,
  CreateTripBody,
  CreateTripResponse,
  FeedbackListResponse,
  GuestLinkInfo,
  GuestTripsResponse,
  MeResponse,
  PatchMetaBody,
  PlacesAutocompleteResponse,
  SendFeedbackBody,
  SendFeedbackResponse,
  TripMeta,
} from './types';
import type { Decision } from './decisions';
import { guestTokenForApiPath } from './guest';

class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

// Session-expiry hook. AuthGate handles the 401 it gets at mount; this
// covers a token expiring *during* a session (a write that 401s mid-use).
// Write surfaces register a redirect-to-/signin; until one is registered
// this is a no-op and the 401 surfaces to the caller as ApiError, so
// current behavior is unchanged. Exported so the raw state fetches in
// trip-state.ts share the same interceptor.
let onUnauthorized: ((path: string) => void) | null = null;
export function setUnauthorizedHandler(fn: ((path: string) => void) | null): void {
  onUnauthorized = fn;
}
export function notifyIfUnauthorized(path: string, status: number): void {
  if (status === 401 && onUnauthorized) onUnauthorized(path);
}

// SPA-fallback guard. With `not_found_handling: single-page-application`
// + `run_worker_first: true`, a /api/* path the Worker doesn't match
// falls through to Workers Assets, which serves index.html as 200 HTML.
// Parsing that as JSON throws a cryptic error; surface the real cause —
// an unmatched API route — instead.
export function assertJsonRoute(path: string, res: Response): void {
  const ct = res.headers.get('Content-Type') || '';
  if (path.startsWith('/api/') && ct.includes('text/html')) {
    throw new ApiError(res.status, null, `API route not matched (got HTML): ${path}`);
  }
}

async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  // Guest share-link sessions ride a header next to the (possibly
  // absent) CF Access cookie; the Worker prefers real membership when
  // both are present. Before ...init.headers so explicit callers win.
  const guestToken = guestTokenForApiPath(path);
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(guestToken ? { 'X-JG-Guest-Token': guestToken } : {}),
      ...init.headers,
    },
  });
  notifyIfUnauthorized(path, res.status);
  assertJsonRoute(path, res);
  if (res.status === 204) return undefined as T;
  let body: unknown = null;
  const ct = res.headers.get('Content-Type') || '';
  if (ct.includes('application/json')) {
    body = await res.json().catch(() => null);
  } else {
    body = await res.text().catch(() => null);
  }
  if (!res.ok) {
    const msg = (body && typeof body === 'object' && 'error' in body
      ? String((body as { error: unknown }).error)
      : `HTTP ${res.status}`);
    throw new ApiError(res.status, body, msg);
  }
  return body as T;
}

export function apiGet<T>(path: string): Promise<T> {
  return request<T>(path, { method: 'GET' });
}

export function apiPost<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: 'POST', body: JSON.stringify(body) });
}

export function apiPatch<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
}

export function apiPut<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: 'PUT', body: JSON.stringify(body) });
}

export function apiDelete<T>(path: string): Promise<T> {
  return request<T>(path, { method: 'DELETE' });
}

// ─── endpoint helpers ────────────────────────────────────────────────

// /api/me is fetched by several surfaces (trips list, app shell, the map's trip
// switcher, the AI email lookup). Cache it: a localStorage warm-start so the
// switcher shows the trips list INSTANTLY (no network wait on first open), plus
// in-flight dedupe so N concurrent mounts share one request. getMe() still
// resolves to the fresh server value — cachedMe() is the synchronous warm-start.
// Exported for AuthGate's lapsed-member heuristic: this key existing means
// a getMe() once succeeded on this browser (it's never cleared on 401).
export const ME_LS = 'jg.me';
let meInFlight: Promise<MeResponse> | null = null;

export function cachedMe(): MeResponse | null {
  try {
    const s = localStorage.getItem(ME_LS);
    return s ? (JSON.parse(s) as MeResponse) : null;
  } catch {
    return null;
  }
}

// Today's rate from one currency into USD (#602), from WaageWu's
// get_fx_rate through the Worker. Rejects with ApiError 404
// (fx_unavailable) when there is no rate.
export interface FxRate {
  from: string;
  to: 'USD';
  rate: number;
  asOf: string | null;
  source: string | null;
}

export function getFxRate(from: string): Promise<FxRate> {
  return apiGet<FxRate>(`/api/me/fx?from=${encodeURIComponent(from)}`);
}

export function getMe(): Promise<MeResponse> {
  if (!meInFlight) {
    meInFlight = apiGet<MeResponse>('/api/me')
      .then((r) => {
        try {
          localStorage.setItem(ME_LS, JSON.stringify(r));
        } catch {
          /* private mode / quota — fine, just no warm-start */
        }
        return r;
      })
      .finally(() => {
        meInFlight = null;
      });
  }
  return meInFlight;
}

export function getTripMeta(tripId: string): Promise<TripMeta> {
  return apiGet<TripMeta>(`/api/trips/${encodeURIComponent(tripId)}`);
}

export function createTrip(body: CreateTripBody): Promise<CreateTripResponse> {
  return apiPost<CreateTripResponse>('/api/trips', body);
}

// Worker returns the updated meta blob FLAT (jsonResponse({...meta, name,
// nameHtml})) at src/worker-trips.js handleTripMeta — same shape as GET.
// Earlier code expected { meta: TripMeta } and pulled res.meta, which
// was undefined → state.meta got set to undefined → planner blank.
export function patchTripMeta(
  tripId: string,
  body: PatchMetaBody,
): Promise<TripMeta> {
  return apiPatch<TripMeta>(
    `/api/trips/${encodeURIComponent(tripId)}/meta`,
    body,
  );
}

// Soft-delete (30-day recovery) and restore a trip.
export function deleteTrip(tripId: string): Promise<unknown> {
  return apiDelete<unknown>(`/api/trips/${encodeURIComponent(tripId)}`);
}
export function restoreTrip(tripId: string): Promise<unknown> {
  return apiPost<unknown>(`/api/trips/${encodeURIComponent(tripId)}/restore`, {});
}

// Global manifest (every entry, all regions — ~14.8k). The "whole catalog"
// browse mode and global search use this.
export function getCatalogManifest(): Promise<CatalogManifest> {
  return apiGet<CatalogManifest>('/api/catalog/manifest.json');
}

// One catalog type's manifest shard (TODO #340). Same entry shape as the
// combined manifest, but only that type's rows — so a surface that needs a
// single type stops paying for all 14.8k entries. The size gap is not
// marginal: the combined manifest is ~10.3 MiB and the destinations shard
// is ~22 KB. Reach for this whenever you were about to write
// `getCatalogManifest().then(m => m.entries.filter(e => e.type === X))`.
export function getCatalogManifestShard(
  type: string,
): Promise<CatalogManifest> {
  return apiGet<CatalogManifest>(
    `/api/catalog/manifest/${encodeURIComponent(type)}.json`,
  );
}

// Trip-scoped manifest — the Worker applies meta.scope (region/country
// allowlists) server-side via filterManifestByTripScope. NOTE the path
// has no `.json` extension; `/api/trips/{id}/catalog/manifest.json` does
// NOT resolve and falls through to the SPA shell.
export function getTripCatalogManifest(tripId: string): Promise<CatalogManifest> {
  return apiGet<CatalogManifest>(
    `/api/trips/${encodeURIComponent(tripId)}/catalog/manifest`,
  );
}

// Trip decisions (the decisions map layer). GET is member/guest-readable; the
// worker returns { decisions }. PUT is owner-only; the response POST is
// member-writable (any viewer registers their own vote/interest).
export function getTripDecisions(tripId: string): Promise<{ decisions: Decision[] }> {
  return apiGet<{ decisions: Decision[] }>(
    `/api/trips/${encodeURIComponent(tripId)}/decisions`,
  );
}

// Upsert one participant's response (vote/interest) on a decision candidate.
// The worker stamps `by` (caller email) + `updatedAt` and returns the full,
// updated { decisions } array (last-write-wins KV upsert keyed participant+cid).
//
// Owner-only: replace the trip's full decision set (used for curation writes
// like "set this photo as the candidate hero"). The worker validates + echoes
// the stored { decisions }.
export function putTripDecisions(
  tripId: string,
  decisions: Decision[],
): Promise<{ decisions: Decision[] }> {
  return apiPut<{ decisions: Decision[] }>(
    `/api/trips/${encodeURIComponent(tripId)}/decisions`,
    { decisions },
  );
}

export function postDecisionResponse(
  tripId: string,
  decisionId: string,
  body: {
    participantId: string;
    cid: string;
    vote?: 'yes' | 'maybe' | 'no';
    interest?: number;
    nights?: number;
    dates?: { start?: string; end?: string };
    comment?: string;
  },
): Promise<{ decisions: Decision[] }> {
  return apiPost<{ decisions: Decision[] }>(
    `/api/trips/${encodeURIComponent(tripId)}/decisions/${encodeURIComponent(decisionId)}/response`,
    body,
  );
}

// Full catalog entity. The global bundle serves per-entity JSON at
// /api/catalog/{type}/{id}.json; the trip-scoped manifest filter does
// not apply to per-entity reads (any entity is fetchable by id).
export function getCatalogEntry(type: string, id: string): Promise<CatalogEntity> {
  return apiGet<CatalogEntity>(
    `/api/catalog/${encodeURIComponent(type)}/${encodeURIComponent(id)}.json`,
  );
}

export interface HeroPhoto {
  url: string;
  photographer?: string;
  photographerUrl?: string;
  source?: string;
}
export interface HeroPhotosResponse {
  photos: HeroPhoto[];
  emptyReason?: string | null;
}

// Hero photos for a destination / region / venue. See CLAUDE.md photo APIs:
// q + type required; location/placeId help venue types. Attribution required
// on render (photographer/source).
export function getHeroPhotos(opts: {
  q: string;
  type: string;
  n?: number;
  location?: string;
  placeId?: string;
  /** "lat,lng" of the venue — lets the Worker resolve a placeId on the fly (2026-09-02). */
  near?: string;
  /** Catalog key "type:id" — the resolver's permanent cache key. */
  entity?: string;
}): Promise<HeroPhotosResponse> {
  const params = new URLSearchParams();
  params.set('q', opts.q);
  params.set('type', opts.type);
  if (opts.n) params.set('n', String(opts.n));
  if (opts.location) params.set('location', opts.location);
  if (opts.placeId) params.set('placeId', opts.placeId);
  if (opts.near) params.set('near', opts.near);
  if (opts.entity) params.set('entity', opts.entity);
  // v2: bypasses edge-cached empty answers from before the placeId resolver.
  params.set('v', '2');
  return apiGet<HeroPhotosResponse>(`/api/photos/hero?${params.toString()}`);
}

// ─── guest share links ───────────────────────────────────────────────

export function listGuestLinks(): Promise<{ links: GuestLinkInfo[] }> {
  return apiGet<{ links: GuestLinkInfo[] }>('/api/me/guest-links');
}

export function createGuestLink(body: {
  scope: 'trips' | 'all' | 'showcase';
  tripIds?: string[];
  label?: string;
}): Promise<{ link: GuestLinkInfo }> {
  return apiPost<{ link: GuestLinkInfo }>('/api/me/guest-links', body);
}

export function revokeGuestLink(token: string): Promise<void> {
  return apiDelete<void>(`/api/me/guest-links/${encodeURIComponent(token)}`);
}

// The shareable showcase (/s/{token}): story fields only, no account.
// src/lib/showcase-share.js on the Worker.
export function getSharedShowcase(token: string): Promise<{
  tripId: string;
  meta: TripMeta;
  state: unknown;
  doc: unknown;
}> {
  return apiGet(`/api/showcase/${encodeURIComponent(token)}`);
}

// Guest-side bootstrap for the /g/{token} landing. Explicit header —
// the token isn't persisted yet at validation time.
export function getGuestTrips(token: string): Promise<GuestTripsResponse> {
  return request<GuestTripsResponse>('/api/guest/trips', {
    method: 'GET',
    headers: { 'X-JG-Guest-Token': token },
  });
}

export function searchPlacesAutocomplete(
  query: string,
  near?: { lat: number; lng: number },
  citiesOnly = false,
): Promise<PlacesAutocompleteResponse> {
  const params = new URLSearchParams();
  params.set('q', query);
  if (citiesOnly) params.set('cities', '1');
  if (near) params.set('near', `${near.lat},${near.lng}`);
  return apiGet<PlacesAutocompleteResponse>(
    `/api/places/autocomplete?${params.toString()}`,
  );
}

// ─── app feedback ────────────────────────────────────────────────────
// Kept in WaageWu's one queue (its docs/feedback); the Worker relays as
// you, with app 'trips'. A repeated opId answers the same item
// (duplicate: true), so a retried send lands once.

export function listFeedback(): Promise<FeedbackListResponse> {
  return apiGet<FeedbackListResponse>('/api/me/feedback');
}

export function sendFeedback(body: SendFeedbackBody): Promise<SendFeedbackResponse> {
  return apiPost<SendFeedbackResponse>('/api/me/feedback', body);
}

export { ApiError };
